import React from "react";
import {
  Button,
  CopyToClipboard,
  Grid,
  Input,
  Popover,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-react";
import { sortedStringify } from "../../../../helpers/jsonUtils";
import { Stage } from "../../../../lusca-search-api/generated-src";

interface VersionState {
  leftSelectedVersion: SelectProps.Option;
  rightSelectedVersion: SelectProps.Option;
  leftVersionOptions: SelectProps.Option[];
  rightVersionOptions: SelectProps.Option[];
  versionOptions: SelectProps.Option[];
}

interface VersionHandlers {
  setLeftSelectedVersion: (option: SelectProps.Option) => void;
  setRightSelectedVersion: (option: SelectProps.Option) => void;
}

interface StageConfig {
  currentStage: Stage;
  compareStage: Stage;
  setCompareStage: React.Dispatch<React.SetStateAction<Stage>>;
  stageOptions: SelectProps.Option[];
  crossStageEnabled: boolean;
  crossStageLoading: boolean;
}

interface OverrideConfig {
  setOverrideId: (id: string) => void;
  identifier: string;
}

interface DataContext {
  filteredObjects: unknown[];
  crossStageData: { cbes: unknown[]; events: unknown[] };
  crossStageFilteredData?: unknown[];
  isCbe: boolean;
  isAccountingGroup: boolean;
  selectedUsecase: SelectProps.Option | null;
  selectedDestination: SelectProps.Option | null;
}

interface VersionSelectionControlsProps {
  versionState: VersionState;
  versionHandlers: VersionHandlers;
  stageConfig: StageConfig;
  overrideConfig: OverrideConfig;
  dataContext: DataContext;
  handleShowHideLines: () => void;
  diffUnchangedLinesOptions: boolean | object;
  shouldHideCopyButtons: boolean;
  shouldShowDiff: boolean;
}

interface OverrideIdSettingsProps {
  identifier: string;
  setOverrideId: (id: string) => void;
  getDefaultOverrideId: () => string;
}

function OverrideIdSettings({
  identifier,
  setOverrideId,
  getDefaultOverrideId,
}: OverrideIdSettingsProps) {
  const [inputValue, setInputValue] = React.useState(() =>
    getDefaultOverrideId()
  );

  return (
    <Popover
      dismissButton={false}
      position="top"
      size="large"
      triggerType="custom"
      content={
        <div style={{ padding: "10px" }}>
          <div style={{ marginBottom: "10px", fontWeight: "bold" }}>
            Override ID
          </div>
          <div style={{ marginBottom: "10px", fontSize: "14px" }}>
            Use a different ID for cross-stage comparison. Useful when
            Beta/Gamma IDs have extra characters (e.g., hashtag suffixes) that
            don&apos;t exist in Prod.
          </div>
          <div style={{ minWidth: "300px", marginBottom: "10px" }}>
            <Input
              value={inputValue}
              onChange={({ detail }) => setInputValue(detail.value)}
              placeholder={`Default: ${identifier}`}
            />
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <Button variant="primary" onClick={() => setOverrideId(inputValue)}>
              Apply
            </Button>
            <Button
              onClick={() => {
                const defaultId = getDefaultOverrideId();
                setInputValue(defaultId);
                setOverrideId(defaultId);
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      }
    >
      <Button
        variant="icon"
        iconName="settings"
        ariaLabel="Override ID settings"
      />
    </Popover>
  );
}

interface StageSelectorComponentProps {
  compareStage: Stage;
  currentStage: Stage;
  setCompareStage: React.Dispatch<React.SetStateAction<Stage>>;
  stageOptions: SelectProps.Option[];
  crossStageEnabled: boolean;
  crossStageLoading: boolean;
  overrideIdSettingsProps: OverrideIdSettingsProps;
}

function StageSelectorComponent({
  compareStage,
  currentStage,
  setCompareStage,
  stageOptions,
  crossStageEnabled,
  crossStageLoading,
  overrideIdSettingsProps,
}: StageSelectorComponentProps) {
  return (
    <SpaceBetween size="xs" direction="horizontal">
      <Select
        selectedOption={{ label: compareStage, value: compareStage }}
        onChange={({ detail }) =>
          setCompareStage(detail.selectedOption?.value as Stage || currentStage)
        }
        options={stageOptions}
        placeholder="Select stage"
      />
      {crossStageEnabled && <OverrideIdSettings {...overrideIdSettingsProps} />}
      {crossStageLoading && <Spinner />}
    </SpaceBetween>
  );
}

export default function VersionSelectionControls({
  versionState,
  versionHandlers,
  stageConfig,
  overrideConfig,
  dataContext,
  handleShowHideLines,
  diffUnchangedLinesOptions,
  shouldHideCopyButtons,
  shouldShowDiff,
}: VersionSelectionControlsProps) {
  const {
    leftSelectedVersion,
    rightSelectedVersion,
    leftVersionOptions,
    rightVersionOptions,
    versionOptions,
  } = versionState;

  const { setLeftSelectedVersion, setRightSelectedVersion } = versionHandlers;

  const {
    currentStage,
    compareStage,
    setCompareStage,
    stageOptions,
    crossStageEnabled,
    crossStageLoading,
  } = stageConfig;

  const { setOverrideId, identifier } = overrideConfig;

  const { filteredObjects, crossStageFilteredData } = dataContext;
  const getDefaultOverrideId = (): string => {
    return crossStageEnabled &&
      (compareStage === Stage.Beta || currentStage === Stage.Beta)
      ? identifier.split("#")[0] // Remove everything after # (ARK IDs for testing)
      : identifier;
  };

  const overrideIdSettingsProps: OverrideIdSettingsProps = {
    identifier,
    setOverrideId,
    getDefaultOverrideId,
  };

  const stageSelectorProps: StageSelectorComponentProps = {
    compareStage,
    currentStage,
    setCompareStage,
    stageOptions,
    crossStageEnabled,
    crossStageLoading,
    overrideIdSettingsProps,
  };

  const handleSelectChange = React.useCallback(
    (setter: (option: SelectProps.Option) => void) =>
      ({ detail }: { detail: { selectedOption: SelectProps.Option } }) => {
        setter(detail.selectedOption);
      },
    []
  );

  const handlePreviousClick = () => {
    if (!leftSelectedVersion) return;

    const currentOptions =
      crossStageEnabled && compareStage !== currentStage
        ? leftVersionOptions
        : versionOptions;
    const n = Number(leftSelectedVersion.value);
    if (n > 0) {
      setLeftSelectedVersion(currentOptions[n - 1]);
      setRightSelectedVersion(
        crossStageEnabled && compareStage !== currentStage
          ? rightVersionOptions[n - 1] || rightVersionOptions[0]
          : currentOptions[n]
      );
    }
  };

  const handleNextClick = () => {
    if (!leftSelectedVersion) return;

    const currentOptions =
      crossStageEnabled && compareStage !== currentStage
        ? leftVersionOptions
        : versionOptions;
    const n = Number(leftSelectedVersion.value);

    // For cross-stage comparisons, allow navigation to the final version
    // For same-stage comparisons, prevent comparing the same item with itself
    const maxIndex =
      crossStageEnabled && compareStage !== currentStage
        ? currentOptions.length - 1
        : currentOptions.length - 2;

    if (n < maxIndex) {
      setLeftSelectedVersion(currentOptions[n + 1]);
      setRightSelectedVersion(
        crossStageEnabled && compareStage !== currentStage
          ? rightVersionOptions[n + 1] ||
              rightVersionOptions[rightVersionOptions.length - 1]
          : currentOptions[n + 2]
      );
    }
  };

  const getLeftCopyText = () => {
    if (!leftSelectedVersion) return "{}";
    return sortedStringify(
      filteredObjects?.[Number(leftSelectedVersion.value)] || {},
      2
    );
  };

  const getRightCopyText = () => {
    if (!rightSelectedVersion) return "{}";

    const rightData =
      crossStageEnabled && compareStage !== currentStage
        ? crossStageFilteredData || filteredObjects
        : filteredObjects;

    return sortedStringify(
      rightData?.[Number(rightSelectedVersion.value)] || {},
      2
    );
  };

  return (
    <SpaceBetween size="s" direction="vertical">
      <Grid
        className="grid-center"
        gridDefinition={[
          { colspan: 2 },
          { colspan: 2 },
          { colspan: 4 },
          { colspan: 2 },
          { colspan: 2 },
        ]}
      >
        <div />
        <Select
          selectedOption={leftSelectedVersion}
          onChange={handleSelectChange(setLeftSelectedVersion)}
          options={
            crossStageEnabled && compareStage !== currentStage
              ? leftVersionOptions
              : versionOptions
          }
          disabled={
            crossStageEnabled && compareStage !== currentStage
              ? leftVersionOptions.length <= 1
              : versionOptions.length <= 1
          }
        />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <SpaceBetween size="s" direction="horizontal">
            <Button
              onClick={handlePreviousClick}
              disabled={
                !leftSelectedVersion || Number(leftSelectedVersion.value) === 0
              }
            >
              Previous
            </Button>
            <Button
              onClick={handleNextClick}
              disabled={
                !leftSelectedVersion ||
                (crossStageEnabled && compareStage !== currentStage
                  ? Number(leftSelectedVersion.value) >=
                    leftVersionOptions.length - 1
                  : Number(leftSelectedVersion.value) >=
                    versionOptions.length - 2)
              }
            >
              Next
            </Button>
          </SpaceBetween>
        </div>

        <Select
          selectedOption={rightSelectedVersion}
          onChange={handleSelectChange(setRightSelectedVersion)}
          options={
            crossStageEnabled && compareStage !== currentStage
              ? rightVersionOptions
              : versionOptions
          }
          disabled={
            crossStageEnabled && compareStage !== currentStage
              ? rightVersionOptions.length <= 1
              : versionOptions.length <= 1
          }
        />
        <StageSelectorComponent {...stageSelectorProps} />
      </Grid>

      <Grid
        className="grid-center"
        gridDefinition={[{ colspan: 5 }, { colspan: 2 }, { colspan: 5 }]}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {!shouldHideCopyButtons && (
            <CopyToClipboard
              copyButtonText="Copy JSON"
              copySuccessText="Copied to clipboard"
              copyErrorText="Error copying"
              textToCopy={getLeftCopyText()}
            />
          )}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Button
            onClick={handleShowHideLines}
            disabled={!shouldShowDiff}
          >
            {diffUnchangedLinesOptions === false
              ? "Hide unchanged lines"
              : "Show unchanged lines"}
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {!shouldHideCopyButtons && (
            <CopyToClipboard
              copyButtonText="Copy JSON"
              copySuccessText="Copied to clipboard"
              copyErrorText="Error copying"
              textToCopy={getRightCopyText()}
            />
          )}
        </div>
      </Grid>
    </SpaceBetween>
  );
}
